import React from 'react';
import { DetailModal } from 'components/DetailModal/DetailModal';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useCareProgramRequest } from 'features/resources/hooks/useCareProgramRequest';
import { EnrollmentDialogState } from 'features/resources/types';
import type { CareProgramDetail } from 'features/resources/types';
import { CareProgramDetailContent } from 'components/CareProgramDetailContent/CareProgramDetailContent';

interface CareProgramDetailPageProps {
  /** Care program data; replace mock with API response. If not provided, uses hook to get from URL */
  program?: CareProgramDetail;
  /** Controls whether the modal is visible. If not provided, uses hook state */
  open?: boolean;
  /** Dismiss callback. If not provided, uses hook handler */
  onClose?: () => void;
  /** Whether user has a pending enrollment request; drives initial CTA state */
  enrollmentRequested?: boolean;
  /** Enrollment confirmation; resolves on success, rejects on failure */
  onConfirmEnroll?: () => Promise<void>;
  /** Called when user confirms opening chat */
  onOpenChat?: () => void;
  /** For Storybook: seeds a specific dialog open on mount */
  defaultDialogState?: EnrollmentDialogState;
  /** Refetch care programs after enrollment request */
  onRefetchCarePrograms?: () => void;
}

export const CareProgramDetailPage: React.FC<CareProgramDetailPageProps> = ({
  program,
  open = false,
  onClose = () => {},
  enrollmentRequested,
  onConfirmEnroll,
  onOpenChat,
  defaultDialogState = EnrollmentDialogState.NONE,
  onRefetchCarePrograms,
}) => {
  const { translation } = useTranslationContext();

  const {
    getItemText,
    dialogState,
    closeDialog,
    buttonText,
    buttonDisabled,
    showChatIcon,
    getDialogContentProps,
    enrollmentDialogProps,
    handleEnrollButtonClick,
  } = useCareProgramRequest({
    enrollmentRequested,
    onConfirmEnroll,
    onOpenChat,
    defaultDialogState,
    enrollmentStatus: program?.enrollmentStatus,
    programId: program?.programId,
    onRefetchCarePrograms,
  });
  const dialogContentProps = getDialogContentProps();

  if (!program) {
    return null;
  }

  return (
    <DetailModal
      open={open}
      onClose={onClose}
      title={translation('program_details')}
      titleId="care-program-detail-title"
      isDialogOpen={dialogState !== 'none'}
    >
      <CareProgramDetailContent
        onClickEnroll={handleEnrollButtonClick}
        getItemText={getItemText}
        program={program}
        enrollmentButtonText={buttonText}
        enrollButtonDisabled={buttonDisabled}
        showChatIcon={showChatIcon}
        statusLabel={(() => {
          const effectiveStatus =
            program.enrollmentStatus ?? (enrollmentRequested ? 'requested' : undefined);
          if (effectiveStatus === 'requested') return translation('requested');
          if (effectiveStatus === 'enrolled') return translation('enrolled');
          return undefined;
        })()}
      />
      <ConfirmationDialog
        open={dialogState !== 'none'}
        onClose={closeDialog}
        {...dialogContentProps}
        {...enrollmentDialogProps}
      />
    </DetailModal>
  );
};
